import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import { API_URL, createApiKey, listApiKeys, revokeApiKey } from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { formatDateTime } from '../../../app/date';
import ConfirmDialog from '../../core/public/ConfirmDialog';

const EXPIRATION_OPTIONS = [30, 90, 365, 0];

const STATUS_COLORS = { ACTIVE: 'success', EXPIRED: 'warning', REVOKED: 'default' };

const mcpUrl = () => `${API_URL.replace(/\/+$/, '')}/mcp`;

const claudeCommand = (secret) =>
  `claude mcp add --transport http eagle ${mcpUrl()} --header "Authorization: Bearer ${secret}"`;

const mcpJsonConfig = (secret) =>
  JSON.stringify(
    { mcpServers: { eagle: { type: 'http', url: mcpUrl(), headers: { Authorization: `Bearer ${secret}` } } } },
    null,
    2
  );

const CopyBlock = ({ label, value, onCopied }) => (
  <Box sx={{ mt: 2 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Box
      sx={{
        position: 'relative',
        mt: 0.5,
        p: 1.5,
        pr: 6,
        borderRadius: 1,
        bgcolor: 'action.hover',
        fontFamily: 'monospace',
        fontSize: 13,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
      }}
    >
      {value}
      <IconButton
        size="small"
        sx={{ position: 'absolute', top: 6, right: 6 }}
        onClick={() => navigator.clipboard.writeText(value).then(onCopied)}
      >
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Box>
  </Box>
);

const ApiKeysPage = () => {
  const { translate } = useTranslation();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showError = (err) => setSnackbar({ open: true, message: err.message, severity: 'error' });
  const showCopied = () => setSnackbar({ open: true, message: translate('settings.api_keys.copied'), severity: 'success' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listApiKeys();
      setKeys(res.data || []);
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await createApiKey({ name: name.trim(), expires_in_days: expiresInDays });
      setCreated(res.data);
      setCreateOpen(false);
      setName('');
      load();
    } catch (err) {
      showError(err);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async () => {
    const key = revoking;
    setRevoking(null);
    try {
      await revokeApiKey(key.id);
      setSnackbar({ open: true, message: translate('settings.api_keys.revoked'), severity: 'success' });
      load();
    } catch (err) {
      showError(err);
    }
  };

  const showOwner = keys.some((k) => !k.is_mine);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {translate('settings.api_keys.mcp_title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {translate('settings.api_keys.mcp_description')}
        </Typography>
        <CopyBlock label={translate('settings.api_keys.mcp_url')} value={mcpUrl()} onCopied={showCopied} />
      </Paper>

      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6">{translate('settings.api_keys.title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {translate('settings.api_keys.description')}
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} sx={{ flexShrink: 0 }}>
            {translate('settings.api_keys.new')}
          </Button>
        </Stack>

        {!loading && keys.length === 0 ? (
          <Alert severity="info">{translate('settings.api_keys.empty')}</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate('settings.api_keys.name')}</TableCell>
                  <TableCell>{translate('settings.api_keys.key')}</TableCell>
                  {showOwner && <TableCell>{translate('settings.api_keys.owner')}</TableCell>}
                  <TableCell>{translate('settings.api_keys.created_at')}</TableCell>
                  <TableCell>{translate('settings.api_keys.last_used_at')}</TableCell>
                  <TableCell>{translate('settings.api_keys.expires_at')}</TableCell>
                  <TableCell>{translate('settings.api_keys.status')}</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {keys.map((k) => (
                  <TableRow key={k.id} hover>
                    <TableCell>{k.name}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{k.key_prefix}…</TableCell>
                    {showOwner && <TableCell>{k.user_email}</TableCell>}
                    <TableCell>{formatDateTime(k.created_at)}</TableCell>
                    <TableCell>{k.last_used_at ? formatDateTime(k.last_used_at) : translate('settings.api_keys.never_used')}</TableCell>
                    <TableCell>{k.expires_at ? formatDateTime(k.expires_at) : translate('settings.api_keys.no_expiration')}</TableCell>
                    <TableCell>
                      <Chip size="small" color={STATUS_COLORS[k.status]} label={translate(`settings.api_keys.status_${k.status.toLowerCase()}`)} />
                    </TableCell>
                    <TableCell align="right">
                      {k.status === 'ACTIVE' && (
                        <Button size="small" color="error" onClick={() => setRevoking(k)}>
                          {translate('settings.api_keys.revoke')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={handleCreate}>
          <DialogTitle>{translate('settings.api_keys.new')}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              margin="normal"
              required
              label={translate('settings.api_keys.name')}
              helperText={translate('settings.api_keys.name_help')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              inputProps={{ maxLength: 80 }}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>{translate('settings.api_keys.expiration')}</InputLabel>
              <Select
                label={translate('settings.api_keys.expiration')}
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
              >
                {EXPIRATION_OPTIONS.map((days) => (
                  <MenuItem key={days} value={days}>
                    {days ? translate('settings.api_keys.expires_in_days', { days }) : translate('settings.api_keys.no_expiration')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Alert severity="info" sx={{ mt: 1 }}>
              {translate('settings.api_keys.scope_notice')}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)} color="inherit">
              {translate('common.cancel')}
            </Button>
            <Button type="submit" variant="contained" disabled={creating || !name.trim()}>
              {translate('settings.api_keys.create')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(created)} fullWidth maxWidth="md">
        <DialogTitle>{translate('settings.api_keys.created_title')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning">{translate('settings.api_keys.created_warning')}</Alert>
          {created && (
            <>
              <CopyBlock label={translate('settings.api_keys.key')} value={created.secret} onCopied={showCopied} />
              <CopyBlock label={translate('settings.api_keys.claude_code')} value={claudeCommand(created.secret)} onCopied={showCopied} />
              <CopyBlock label={translate('settings.api_keys.json_config')} value={mcpJsonConfig(created.secret)} onCopied={showCopied} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Tooltip title={translate('settings.api_keys.created_warning')}>
            <Button variant="contained" onClick={() => setCreated(null)}>
              {translate('settings.api_keys.done')}
            </Button>
          </Tooltip>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(revoking)}
        title={translate('settings.api_keys.revoke_title')}
        message={revoking ? translate('settings.api_keys.revoke_message', { name: revoking.name }) : ''}
        confirmText={translate('settings.api_keys.revoke')}
        cancelText={translate('common.cancel')}
        onConfirm={handleRevoke}
        onCancel={() => setRevoking(null)}
      />

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApiKeysPage;
